import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';
import { 
  FiHelpCircle, FiMail, FiMessageCircle, FiBook, FiAlertCircle, 
  FiSearch, FiChevronDown, FiUser, FiFileText, FiUsers, 
  FiSettings, FiShield, FiCreditCard, FiPhone
} from 'react-icons/fi';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './Help.css';

const Help = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const [supportSubject, setSupportSubject] = useState('');
  const [supportCategory, setSupportCategory] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);

  const categories = [
    { id: 'all', name: 'Todas', icon: <FiBook /> },
    { id: 'account', name: 'Cuenta', icon: <FiUser /> },
    { id: 'publishing', name: 'Publicaciones', icon: <FiFileText /> },
    { id: 'community', name: 'Comunidad', icon: <FiUsers /> },
    { id: 'settings', name: 'Configuración', icon: <FiSettings /> },
    { id: 'security', name: 'Seguridad', icon: <FiShield /> },
    { id: 'billing', name: 'Pagos', icon: <FiCreditCard /> }
  ];

  const faqItems = [
    // CUENTA
    {
      category: 'account',
      question: '¿Cómo creo una cuenta en Graphyra?',
      answer: 'Puedes crear una cuenta haciendo clic en "Registrarse" en la página principal. Completa el formulario con tu nombre, email y contraseña. También puedes registrarte usando Google OAuth para un proceso más rápido.'
    },
    {
      category: 'account',
      question: '¿Cómo cambio mi foto de perfil?',
      answer: 'Ve a tu perfil, haz clic en "Editar Perfil" y luego en el icono de cámara sobre tu foto actual. Puedes subir una imagen desde tu dispositivo (máximo 5MB, formatos JPG, PNG o GIF).'
    },
    {
      category: 'account',
      question: '¿Puedo cambiar mi nombre de usuario?',
      answer: 'Sí, puedes cambiar tu nombre de usuario desde "Editar Perfil". El nuevo nombre debe ser único y tener al menos 3 caracteres. Solo puede contener letras, números y guiones.'
    },
    {
      category: 'account',
      question: '¿Cómo verifico mi correo electrónico?',
      answer: 'Al registrarte, recibirás un correo de verificación. Haz clic en el enlace del correo para verificar tu cuenta. Si no lo recibes, revisa tu carpeta de spam o solicita un nuevo correo desde configuración.'
    },
    {
      category: 'account',
      question: '¿Puedo tener múltiples cuentas?',
      answer: 'No, cada usuario debe tener una sola cuenta. Crear múltiples cuentas va contra nuestros términos de servicio y puede resultar en la suspensión de todas las cuentas asociadas.'
    },
    // PUBLICACIONES
    {
      category: 'publishing',
      question: '¿Cómo puedo crear una publicación?',
      answer: 'Para crear publicaciones necesitas permisos especiales. Graphyra es una plataforma de contenido científico y educativo curado. Si eres un profesional o académico y deseas publicar, contacta al equipo a través del formulario de soporte.'
    },
    {
      category: 'publishing',
      question: '¿Cómo guardo publicaciones en favoritos?',
      answer: 'Haz clic en el ícono de marcador (guardar) en cualquier publicación. Puedes acceder a tus publicaciones guardadas desde "Favoritos" en tu perfil o en el menú principal.'
    },
    {
      category: 'publishing',
      question: '¿Cómo comento en una publicación?',
      answer: 'Desplázate hasta el final de la publicación y escribe tu comentario en el campo de texto. Puedes mencionar a otros usuarios usando @ seguido de su nombre de usuario.'
    },
    {
      category: 'publishing',
      question: '¿Puedo editar o eliminar mis comentarios?',
      answer: 'Sí, puedes editar tus comentarios haciendo clic en el icono de edición, o eliminarlos usando el icono de papelera. Los comentarios editados mostrarán una marca de "editado".'
    },
    {
      category: 'publishing',
      question: '¿Qué tipos de contenido puedo encontrar?',
      answer: 'Graphyra ofrece artículos científicos, videos educativos, infografías, podcasts, cursos en Graphyra Academy, y contenido multimedia curado por expertos en diversas áreas del conocimiento.'
    },
    // COMUNIDAD
    {
      category: 'community',
      question: '¿Qué son las insignias de verificación?',
      answer: 'Las insignias indican el nivel de verificación: Sin verificación, Verificado Básico (gris), Profesional (verde), Académico (azul), Institucional (morado), Staff (cian) y Oficial (rojo). Representan la autenticidad y credibilidad de la cuenta.'
    },
    {
      category: 'community',
      question: '¿Cómo obtengo una insignia de verificación?',
      answer: 'Contacta al equipo a través del formulario de soporte con documentación que respalde tu identidad profesional, académica o institucional. El proceso puede tomar de 3 a 7 días hábiles.'
    },
    {
      category: 'community',
      question: '¿Cómo reporto contenido inapropiado?',
      answer: 'Usa el botón de reportar (tres puntos) en cualquier publicación o comentario. Selecciona la razón del reporte y nuestro equipo lo revisará en menos de 24 horas.'
    },
    {
      category: 'community',
      question: '¿Puedo seguir a otros usuarios?',
      answer: 'Actualmente Graphyra se enfoca en el contenido más que en perfiles individuales. Puedes guardar publicaciones de autores que te interesen y ver su perfil para encontrar más contenido.'
    },
    {
      category: 'community',
      question: '¿Cómo funcionan las reacciones?',
      answer: 'Puedes dar "me gusta" a publicaciones y comentarios. También puedes reaccionar a comentarios con emojis. Estas interacciones ayudan a destacar contenido valioso para la comunidad.'
    },
    // CONFIGURACIÓN
    {
      category: 'settings',
      question: '¿Cómo cambio mi contraseña?',
      answer: 'Ve a "Editar Perfil", selecciona la pestaña "Seguridad" e ingresa tu contraseña actual seguida de la nueva contraseña. Debe tener al menos 6 caracteres.'
    },
    {
      category: 'settings',
      question: '¿Puedo cambiar mi correo electrónico?',
      answer: 'Sí, desde "Editar Perfil" puedes actualizar tu correo electrónico. Tendrás que verificar el nuevo correo antes de que el cambio sea efectivo.'
    },
    {
      category: 'settings',
      question: '¿Cómo gestiono mis notificaciones?',
      answer: 'Las notificaciones te mantienen informado sobre interacciones con tu contenido, menciones y actualizaciones importantes. Puedes ver todas tus notificaciones haciendo clic en el icono de campana en la barra superior.'
    },
    {
      category: 'settings',
      question: '¿Cómo actualizo mi información profesional?',
      answer: 'En "Editar Perfil", la pestaña "Información Profesional" te permite agregar tu cargo, organización, departamento, título académico y especialización.'
    },
    // SEGURIDAD
    {
      category: 'security',
      question: '¿Cómo puedo suspender mi cuenta temporalmente?',
      answer: 'Ve a "Configuración de Cuenta" y selecciona "Suspender Cuenta". Tu perfil y publicaciones se ocultarán hasta que decidas reactivarla iniciando sesión nuevamente.'
    },
    {
      category: 'security',
      question: '¿Puedo eliminar mi cuenta permanentemente?',
      answer: 'Sí, desde "Configuración de Cuenta" puedes eliminar tu cuenta de forma permanente. Esta acción es irreversible y todos tus datos serán eliminados. Las cuentas con roles administrativos no pueden ser eliminadas.'
    },
    {
      category: 'security',
      question: '¿Mi información está segura?',
      answer: 'Sí, usamos encriptación de grado bancario para proteger tus datos. Las contraseñas están hasheadas con bcrypt y nunca almacenamos información sensible en texto plano. Lee nuestra Política de Privacidad para más detalles.'
    },
    {
      category: 'security',
      question: '¿Qué hago si olvidé mi contraseña?',
      answer: 'Haz clic en "¿Olvidaste tu contraseña?" en la página de inicio de sesión. Recibirás un correo con instrucciones para restablecer tu contraseña.'
    },
    {
      category: 'security',
      question: '¿Qué son las sesiones activas?',
      answer: 'Cada vez que inicias sesión en un dispositivo, se crea una sesión. Puedes ver y cerrar sesiones activas desde configuración de seguridad para proteger tu cuenta.'
    },
    // PAGOS
    {
      category: 'billing',
      question: '¿Graphyra es gratuito?',
      answer: 'Sí, Graphyra es completamente gratuito. Puedes acceder a todo el contenido educativo y científico, incluyendo Graphyra Academy, sin ningún costo.'
    },
    {
      category: 'billing',
      question: '¿Ofrecen membresías premium?',
      answer: 'Actualmente todos los servicios de Graphyra son gratuitos. En el futuro podríamos introducir características premium opcionales para apoyar a creadores de contenido.'
    },
    {
      category: 'billing',
      question: '¿Cómo se financia Graphyra?',
      answer: 'Graphyra es un proyecto educativo sin fines de lucro. Actualmente es completamente gratuito. En el futuro, para mantener la plataforma y seguir ofreciendo contenido de calidad gratuito, implementaremos un sistema de anuncios respetuosos y no intrusivos, junto con opciones de membresía premium opcional para quienes deseen una experiencia sin anuncios.'
    }
  ];

  const contactMethods = [
    {
      icon: <FiMail size={24} />,
      title: 'Email',
      description: 'contact.graphyra@gmail.com',
      action: 'Enviar Email',
      link: 'mailto:contact.graphyra@gmail.com'
    },
    {
      icon: <FiPhone size={24} />,
      title: 'Soporte',
      description: 'Tiempo de respuesta: 24-48h',
      action: 'Formulario de Soporte',
      link: '#support-form'
    },
    {
      icon: <FiBook size={24} />,
      title: 'Documentación',
      description: 'Guías y tutoriales completos',
      action: 'Ver Documentación',
      link: '/docs'
    }
  ];

  // Filtrar FAQs según búsqueda y categoría
  const filteredFaqs = useMemo(() => {
    let filtered = faqItems;

    // Filtrar por categoría
    if (activeCategory !== 'all') {
      filtered = filtered.filter(item => item.category === activeCategory);
    }

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeCategory, searchQuery, faqItems]);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Verificar si el usuario tiene un rol especial (staff/admin)
  const isStaffOrAdmin = user && (
    ['superadmin', 'ceo', 'coo', 'head_of_content', 'community_lead', 'moderator'].includes(user.hierarchical_role)
  );

  // Si es staff/admin, redirigir al home
  if (isStaffOrAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="help-container">
      <div className="help-header">
        <FiHelpCircle size={40} />
        <h1>Centro de Ayuda</h1>
        <p>Encuentra respuestas a tus preguntas y contacta con soporte</p>

        {/* Barra de búsqueda */}
        <div className="help-search">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar en preguntas frecuentes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Métodos de contacto */}
      <section className="help-section">
        <h2>¿Cómo podemos ayudarte?</h2>
        <div className="contact-methods">
          {contactMethods.map((method, index) => (
            <div key={index} className="contact-card">
              <div className="contact-icon">{method.icon}</div>
              <h3>{method.title}</h3>
              <p>{method.description}</p>
              <a 
                href={method.link} 
                className="contact-action"
                onClick={(e) => {
                  if (method.link === '#support-form') {
                    e.preventDefault();
                    document.getElementById('support-form')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {method.action}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Categorías */}
      <section className="help-section">
        <h2>Categorías</h2>
        <div className="help-categories">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.icon}
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="help-section">
        <h2>
          Preguntas Frecuentes
          {searchQuery && (
            <span className="search-results">
              {' '}({filteredFaqs.length} resultado{filteredFaqs.length !== 1 ? 's' : ''})
            </span>
          )}
        </h2>

        {filteredFaqs.length === 0 ? (
          <div className="no-results">
            <FiAlertCircle size={48} />
            <p>No se encontraron resultados para "{searchQuery}"</p>
            <button 
              className="btn-clear-search"
              onClick={() => setSearchQuery('')}
            >
              Limpiar búsqueda
            </button>
          </div>
        ) : (
          <div className="faq-list">
            {filteredFaqs.map((item, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <span>{item.question}</span>
                  <FiChevronDown className="faq-icon" />
                </div>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Formulario de soporte */}
      <section className="help-section" id="support-form">
        <h2>Formulario de Soporte</h2>
        <div className="support-form-container">
          <form className="support-form" onSubmit={async (e) => {
            e.preventDefault();

            if (!user) {
              toast.error('Inicia sesión para enviar soporte');
              return;
            }

            const subject = supportSubject.trim();
            const message = supportMessage.trim();
            const category = String(supportCategory || '').trim();

            if (!subject) {
              toast.error('El asunto es requerido');
              return;
            }
            if (!category) {
              toast.error('Selecciona una categoría');
              return;
            }
            if (!message) {
              toast.error('El mensaje es requerido');
              return;
            }

            try {
              setSupportSubmitting(true);
              await usersAPI.createSupportTicket({ subject, category, message, priority: 'medium' });
              toast.success('Mensaje enviado. Te responderemos pronto.');
              setSupportSubject('');
              setSupportCategory('');
              setSupportMessage('');
            } catch (error) {
              console.error('Error creating support ticket:', error);
              toast.error(error.response?.data?.error || 'Error al enviar el mensaje');
            } finally {
              setSupportSubmitting(false);
            }
          }}>
            <div className="form-group">
              <label htmlFor="subject">Asunto</label>
              <input
                type="text"
                id="subject"
                placeholder="¿En qué podemos ayudarte?"
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Categoría</label>
              <select
                id="category"
                value={supportCategory}
                onChange={(e) => setSupportCategory(e.target.value)}
                required
              >
                <option value="">Selecciona una categoría</option>
                <option value="account">Problemas con mi cuenta</option>
                <option value="technical">Problema técnico</option>
                <option value="content">Reporte de contenido</option>
                <option value="verification">Verificación de cuenta</option>
                <option value="other">Otro</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">Mensaje</label>
              <textarea
                id="message"
                rows="6"
                placeholder="Describe tu problema o pregunta con el mayor detalle posible..."
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <button type="submit" className="btn-submit" disabled={supportSubmitting}>
              <FiMail size={18} />
              {supportSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
            </button>
          </form>

          <div className="support-info">
            <FiAlertCircle size={24} />
            <h3>Tiempo de respuesta</h3>
            <p>
              Nuestro equipo de soporte responde a todos los mensajes en un plazo de 24-48 horas.
              Para problemas urgentes, revisa nuestras preguntas frecuentes o contáctanos por email.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Help;
